const isPlaceholder = (a) => {
    return a != null && typeof a === 'object' && a['@@functional/placeholder'] === true;
};

const curry1 = (fn) => {
    return function f1(a) {
        if (arguments.length === 0 || isPlaceholder(a)) {
            return f1;
        }
        // eslint-disable-next-line prefer-rest-params
        return fn.apply(this, arguments);
    };
};

const curry2 = (fn) => {
    return function f2(a, b) {
        switch (arguments.length) {
            case 0: return f2;
            case 1: return isPlaceholder(a) ? f2 : curry1((_b) => fn(a, _b));

            default:
                return isPlaceholder(a) && isPlaceholder(b)
                    ? f2
                    : isPlaceholder(a)
                        ? curry1((_a) => fn(_a, b))
                        : isPlaceholder(b)
                            ? curry1((_b) => fn(a, _b))
                            : fn(a, b);
        }
    };
};

const nth =
    curry2((offset, list) => {
        const idx = offset < 0 ? list.length + offset : offset;
        return Object.prototype.toString.call(list) === '[object String]' ? list.charAt(idx) : list[idx];
    });

const arity = (n, fn) => {
    /* eslint-disable no-unused-vars */
    switch (n) {
        case 0: return function() { return fn.apply(this, arguments); }; // eslint-disable-line prefer-rest-params
        case 1: return function(a0) { return fn.apply(this, arguments); }; // eslint-disable-line prefer-rest-params
        case 2: return function(a0, a1) { return fn.apply(this, arguments); }; // eslint-disable-line prefer-rest-params
        case 3: return function(a0, a1, a2) { return fn.apply(this, arguments); }; // eslint-disable-line prefer-rest-params
        case 4: return function(a0, a1, a2, a3) { return fn.apply(this, arguments); }; // eslint-disable-line prefer-rest-params
        case 5: return function(a0, a1, a2, a3, a4) { return fn.apply(this, arguments); }; // eslint-disable-line prefer-rest-params
        case 6: return function(a0, a1, a2, a3, a4, a5) { return fn.apply(this, arguments); }; // eslint-disable-line prefer-rest-params
        case 7: return function(a0, a1, a2, a3, a4, a5, a6) { return fn.apply(this, arguments); }; // eslint-disable-line prefer-rest-params
        case 8: return function(a0, a1, a2, a3, a4, a5, a6, a7) { return fn.apply(this, arguments); }; // eslint-disable-line prefer-rest-params
        case 9: return function(a0, a1, a2, a3, a4, a5, a6, a7, a8) { return fn.apply(this, arguments); }; // eslint-disable-line prefer-rest-params
        case 10: return function(a0, a1, a2, a3, a4, a5, a6, a7, a8, a9) { return fn.apply(this, arguments); }; // eslint-disable-line prefer-rest-params
        default: throw new Error('First argument to _arity must be a non-negative integer no greater than ten');
    }
};

const type =
    curry1((val) => {
        return val === null ? 'Null' : val === undefined ? 'Undefined' : Object.prototype.toString.call(val).slice(8, -1);
    });

const cloneRegExp = (pattern) => {
    return new RegExp(pattern.source, (pattern.global ? 'g' : '') + (pattern.ignoreCase ? 'i' : '') + (pattern.multiline ? 'm' : '') + (pattern.sticky ? 'y' : '') + (pattern.unicode ? 'u' : ''));
};

// eslint-disable-next-line no-underscore-dangle
const _clone = (value, refFrom, refTo, deep) => {
    const copy = function copy(copiedValue) {
        const len = refFrom.length;
        let idx = 0;

        while (idx < len) {
            if (value === refFrom[idx]) {
                return refTo[idx];
            }

            idx += 1;
        }

        refFrom[idx + 1] = value;
        refTo[idx + 1] = copiedValue;

        for (const key in value) {
            copiedValue[key] = deep ? _clone(value[key], refFrom, refTo, true) : value[key];
        }

        return copiedValue;
    };

    switch (type(value)) {
        case 'Object': return copy({});
        case 'Array': return copy([]);
        case 'Date': return new Date(value.valueOf());
        case 'RegExp': return cloneRegExp(value);
        default: return value;
    }
};

/* ------------------------------------------for export----------------------------------- */
export const pick =
    curry2((names, obj) => {
        const result = {};
        let idx = 0;

        while (idx < names.length) {
            if (names[idx] in obj) {
                result[names[idx]] = obj[names[idx]];
            }

            idx += 1;
        }

        return result;
    });

export const path =
    curry2((pathAr, obj) => {
        let val = obj;
        let idx = 0;
        let p;

        while (idx < pathAr.length) {
            if (val == null) {
                return;
            }

            p = pathAr[idx];
            if (Number.isInteger(p) || p << 0 === p) {
                val = nth(p, val);
            } else {
                val = val[p];
            }
            idx += 1;
        }

        return val;
    });

export const prop = curry2((p, obj) => path([p], obj));

export const pipe = (...args) => {
    if (args.length === 0) {
        throw new Error('pipe requires at least one argument');
    }

    return arity(args[0].length, args
        .slice(1, args.length)
        .reduce((f, g) => (...rest) => g.call(this, f.apply(this, rest)), args[0]));
};

export const compose = (...args) => {
    if (args.length === 0) {
        throw new Error('compose requires at least one argument');
    }

    return pipe.apply(this, args.reverse());
};

export const identity = curry1(x => x);

export const isNil = curry1(x => x == null);

export const omit =
    curry2((names, ob) => {
        const resultOmit = {};
        const indexOmit = {};
        let idx = 0;
        const len = names.length;

        while (idx < len) {
            indexOmit[names[idx]] = 1;
            idx += 1;
        }

        for (const propOmit in ob) {
            // eslint-disable-next-line no-prototype-builtins
            if (!indexOmit.hasOwnProperty(propOmit)) {
                resultOmit[propOmit] = ob[propOmit];
            }
        }

        return resultOmit;
    });

export const range =
    curry2((from, to) => {
        if (!(Object.prototype.toString.call(from) === '[object Number]' && Object.prototype.toString.call(to) === '[object Number]')) {
            throw new TypeError('Both arguments to range must be numbers');
        }

        const result = [];
        let n = from;

        while (n < to) {
            result.push(n);
            n += 1;
        }

        return result;
    });

export const isEmpty =
    curry1((x) => {
        if (x == null) {
            return true;
        }

        if (Object.prototype.toString.call(x) === '[object ArrayBuffer]') {
            return false;
        }

        if (Array.isArray(x) || typeof x === 'string' || typeof x.splice === 'function'
        || typeof x.slice === 'function' || Object.prototype.toString.call(x) === '[object Arguments]'
        || Object.prototype.toString.call(x).includes('Array')) {
            return !x.length;
        }

        if (Object.prototype.toString.call(x) === '[object Map]' || Object.prototype.toString.call(x) === '[object Set]') {
            return !x.size;
        }

        const isProto = (value) => {
            const xConstructor = value && value.constructor;
            const proto = typeof xConstructor === 'function' && xConstructor.prototype || Object.prototype;

            return value === proto;
        };

        if (isProto(x)) {
            return !Object.keys(x).length;
        }

        for (const key in x) {
            if (Object.prototype.hasOwnProperty.call(x, key)) {
                return false;
            }
        }

        if (typeof x === 'number') {
            return false;
        }

        return true;
    });

// to use instead ramda equals
export const compareObjects = (obj1, obj2) => {
    const orderKeys = (obj) => {
        // eslint-disable-next-line eqeqeq
        if (obj == null || typeof obj !== 'object' || !Object.keys(obj).length) {
            return obj;
        }

        return Object.keys(obj).sort()
            .reduce((result, key) => {
                result[key] = orderKeys(obj[key]);

                return result;
            }, {}
        );
    };

    return JSON.stringify(orderKeys(obj1)) === JSON.stringify(orderKeys(obj2));
};

export const clone =
    curry1((value) => {
        return value != null && typeof value.clone === 'function' ? value.clone() : _clone(value, [], [], true);
    });
